<script setup lang="ts">
  import { computed, ref, type Component } from "vue";
  import { storeToRefs } from "pinia";
  import { Archive, Copy, Images, Tag, Tags } from "lucide-vue-next";
  import { ListContainer, ListItem } from "@/components/episto-ui/list";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useMAssetLibraryStore, type LibraryView } from "@stores/mAssetLibrary";
  import { renamedTagName, splitFacet } from "@utils/assetTags";
  import type { AssetTag } from "@/types";

  const emit = defineEmits<{ upload: [] }>();

  const assetLibraryStore = useMAssetLibraryStore();
  const { view, counts, allAssetTags, tagCounts, activeTagFilters, isLoadingTags } = storeToRefs(assetLibraryStore);
  const { toggleTagFilter, setView, renameTag } = assetLibraryStore;

  const viewItems = computed<{ value: LibraryView; label: string; icon: Component; count: number }[]>(() => [
    { value: "in", label: "In library", icon: Images, count: counts.value.in_library },
    { value: "untagged", label: "Untagged", icon: Tags, count: counts.value.untagged },
    { value: "duplicates", label: "Duplicates", icon: Copy, count: counts.value.duplicates },
    { value: "out", label: "Removed from library", icon: Archive, count: counts.value.out_of_library },
  ]);

  // Only the tags of the assets matching the filters; picked tags stay listed so they can be unpicked.
  const matchingTags = computed(() => {
    const matching = tagCounts.value;
    if (!matching) return allAssetTags.value;
    return allAssetTags.value.filter((tag) => matching[tag.name] || activeTagFilters.value.includes(tag.name));
  });

  const matchingCount = (tag: AssetTag) => (tagCounts.value ? (tagCounts.value[tag.name] ?? 0) : (tag.images_count ?? 0));

  // Tags used by a single image across the library are usually typos or variants of another tag.
  const isSingleUse = (tag: AssetTag) => (tag.images_count ?? 0) <= 1;
  const showSingleUseOnly = ref(false);
  const singleUseTags = computed(() => allAssetTags.value.filter(isSingleUse));
  const visibleTags = computed(() =>
    showSingleUseOnly.value ? matchingTags.value.filter(isSingleUse) : matchingTags.value,
  );

  // Faceted tags ("pays:france") are listed under their facet, free-text ones last.
  const tagGroups = computed(() => {
    const groups = new Map<string | null, AssetTag[]>();
    visibleTags.value.forEach((tag) => {
      const { facet } = splitFacet(tag.name);
      groups.set(facet, [...(groups.get(facet) ?? []), tag]);
    });
    const facets = [...groups.keys()].filter((facet): facet is string => facet !== null).sort();
    return [
      ...facets.map((facet) => ({ key: facet, label: facet, tags: groups.get(facet) ?? [] })),
      ...(groups.has(null) ? [{ key: "", label: facets.length ? "Other tags" : "", tags: groups.get(null) ?? [] }] : []),
    ];
  });

  const draftNames = ref<Record<number, string>>({});
  const pendingMerge = ref<{ tag: AssetTag; into: string } | null>(null);

  const displayName = (tag: AssetTag) => draftNames.value[tag.id] ?? splitFacet(tag.name).value;

  const editTagName = (tag: AssetTag, value: string) => {
    draftNames.value = { ...draftNames.value, [tag.id]: value };
  };

  const restoreTagName = (tag: AssetTag) => {
    const { [tag.id]: _discarded, ...rest } = draftNames.value;
    draftNames.value = rest;
  };

  const saveTagName = async (tag: AssetTag) => {
    const typed = draftNames.value[tag.id];
    restoreTagName(tag);
    if (typed === undefined) return;
    const name = renamedTagName(tag.name, typed);
    if (!name || name === tag.name) return;

    if (allAssetTags.value.some((other) => other.id !== tag.id && other.name === name)) {
      pendingMerge.value = { tag, into: name };
      return;
    }
    await renameTag(tag, name);
  };

  const confirmMerge = async () => {
    if (!pendingMerge.value) return;
    const { tag, into } = pendingMerge.value;
    pendingMerge.value = null;
    await renameTag(tag, into);
  };
</script>

<template>
  <ListContainer
    class="max-h-[calc(100vh-40px)]"
    element-name="assets"
    :add-element="() => emit('upload')"
    :show-header="false">
    <template #elements>
      <div class="heading-label px-1.5 pb-1 pt-1">Library</div>
      <ListItem
        v-for="(item, index) in viewItems"
        :key="item.value"
        :index="index"
        :icon="item.icon"
        :element-title="item.label"
        :select-element="() => setView(item.value)"
        :is-element-selected="() => view === item.value">
        <template #details>
          <span class="text-xs text-muted-foreground">{{ item.count }}</span>
        </template>
      </ListItem>

      <div class="flex items-center justify-between px-1.5 pb-1 pt-4">
        <span class="heading-label">Tags</span>
        <button
          v-if="singleUseTags.length"
          type="button"
          class="text-xs text-muted-foreground underline-offset-2 hover:underline"
          :class="{ 'font-medium text-primary': showSingleUseOnly }"
          :aria-pressed="showSingleUseOnly"
          @click="showSingleUseOnly = !showSingleUseOnly">
          {{ showSingleUseOnly ? "Show all" : `Single-use (${singleUseTags.length})` }}
        </button>
      </div>
      <p class="px-1.5 pb-1 text-2xs text-foreground-subtle">Rename a tag to “facet:value” (theme:yoga) to file it under a facet.</p>
      <template
        v-for="group in tagGroups"
        :key="group.key">
        <div
          v-if="group.label"
          class="px-1.5 pb-0.5 pt-2 text-2xs font-medium uppercase tracking-wide text-foreground-subtle">
          {{ group.label }}
        </div>
        <ListItem
          v-for="(tag, index) in group.tags"
          :key="tag.id"
          :index="index"
          :icon="Tag"
          :element-title="displayName(tag)"
          :select-element="() => toggleTagFilter(tag.name)"
          :is-element-selected="() => activeTagFilters.includes(tag.name)"
          :edit-element-title="(_index, value) => editTagName(tag, value)"
          :save-title-changes="() => saveTagName(tag)"
          :restore-title-changes="() => restoreTagName(tag)">
          <template #details>
            <span class="text-xs text-muted-foreground">{{ matchingCount(tag) }}</span>
          </template>
        </ListItem>
      </template>
      <p
        v-if="!visibleTags.length && !isLoadingTags"
        class="px-1.5 py-2 text-xs text-muted-foreground">
        {{ allAssetTags.length ? "No tags on these assets" : "No tags yet" }}
      </p>
    </template>
  </ListContainer>

  <ConfirmDialog
    :open="!!pendingMerge"
    :title="`Merge “${pendingMerge?.tag.name}” into “${pendingMerge?.into}”?`"
    confirm-label="Merge"
    variant="default"
    @update:open="(open) => !open && (pendingMerge = null)"
    @confirm="confirmMerge">
    Every asset tagged “{{ pendingMerge?.tag.name }}” gets “{{ pendingMerge?.into }}” instead, and “{{
      pendingMerge?.tag.name
    }}” disappears.
  </ConfirmDialog>
</template>
